// AIT-529 — invited-signup connect mode.
//
// A client who arrived through an affiliate invite link never lands in a
// workspace: after the grant completes they go straight to a standalone
// "connect a channel" screen and, once the channel is live, to a standalone
// done screen. They may never open the workspace at all.
//
// The Meta OAuth round-trip leaves and re-enters the app, so the mode has to
// survive a full page navigation — same tab-scoped sessionStorage rationale
// as invite-storage.ts.

import { workspaceUrl } from '@/lib/workspace-url';

const INVITE_CONNECT_STORAGE_KEY = 'hookmyapp_invite_connect_v1';

function hasSessionStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
}

/** Mark this tab as connecting through the standalone invite flow. */
export function setInviteConnectPending(): void {
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.setItem(INVITE_CONNECT_STORAGE_KEY, '1');
  } catch {
    // Quota / privacy mode: fall back to the normal workspace destination.
  }
}

export function isInviteConnectPending(): boolean {
  if (!hasSessionStorage()) return false;
  try {
    return window.sessionStorage.getItem(INVITE_CONNECT_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Clear the mode. Call once the flow reaches its terminal screen. */
export function clearInviteConnectPending(): void {
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.removeItem(INVITE_CONNECT_STORAGE_KEY);
  } catch {
    // Ignore — worst case the next connect in this tab lands standalone.
  }
}

/**
 * Where the Meta auth code gets exchanged. Normally the workspace-scoped
 * /w/:ws/channels/connecting page (inside the app chrome); for an invited
 * signup the standalone /invite/connecting/:ws twin, which renders the same
 * MetaConnectingPage without ever mounting the workspace shell.
 *
 * `search` is the already-encoded query string (no leading '?').
 */
export function connectingPath(ws: string, search: string): string {
  if (isInviteConnectPending()) return `/invite/connecting/${ws}?${search}`;
  return workspaceUrl(ws, `channels/connecting?${search}`);
}

/** Exposed so tests can clear state between cases without hard-coding the string. */
export const INVITE_CONNECT_STORAGE_KEY_FOR_TESTS = INVITE_CONNECT_STORAGE_KEY;
